import {
	Canvas,
	Editor as CraftJsEditor,
	ReduceCompType,
	Resolver,
	SerializedNode
} from '@craftjs/core';
import '@descope/page-editor-components/dist/style.css';
import { isEmpty } from 'lodash';
import React, { ReactElement, useMemo } from 'react';
import { IScreenMetadata } from 'types/flows';
import Editor from './Editor';
import { CanvasContextProvider } from './Editor/ActionsBar/CanvasContext';
import json from './Editor/defaultState.json';
import { RenderNode } from './RenderNode';
// eslint-disable-next-line import/no-namespace
import * as editorComponents from './editorComponents';

// we want to map the components names to use the original names so it will play nice with the renderer service
const mappedComponents = Object.keys(editorComponents).reduce((acc, key) => {
	Object.defineProperty(
		acc,
		key.replace(/^Editor/, ''),
		Object.getOwnPropertyDescriptor(editorComponents, key)!
	);

	return acc;
}, {}) as Resolver;

// Resolve deprecated components to their new implementation
mappedComponents.CodeInput = mappedComponents.Code;
mappedComponents.PhoneInput = mappedComponents.Phone;
mappedComponents.CustomIdentifierInput = mappedComponents.CustomLoginIdInput;

const getNodeType = (type: ReduceCompType) =>
	typeof type === 'object' && type.resolvedName ? type.resolvedName : type;

const restoreType = (type: ReduceCompType, resolver: Resolver) => {
	const nodeType = getNodeType(type);
	// eslint-disable-next-line no-nested-ternary
	return nodeType === 'Canvas' ? Canvas : resolver[nodeType as any];
};

const PageEditor: React.FC<{
	initialScreen?: IScreenMetadata;
	onDirty?: (isDirty: boolean) => void;
	updatePageCallback?: (func: () => IScreenMetadata) => void;
	header?: ReactElement;
}> = ({ initialScreen, updatePageCallback, header, onDirty }) => {
	const htmlTemplate = (
		isEmpty(initialScreen?.htmlTemplate) ? json : initialScreen?.htmlTemplate
	) as IScreenMetadata['htmlTemplate'];

	const missingNode = useMemo(
		() =>
			Object.values(htmlTemplate!).find(
				(node: SerializedNode) => !restoreType(node.type, mappedComponents)
			),
		[htmlTemplate]
	);

	// later we can present a proper error message
	if (missingNode) {
		throw Error(
			`An Error occurred while deserializing components: Cannot find component "${getNodeType(
				missingNode.type
			)}" in resolver map`
		);
	}

	return (
		<CraftJsEditor resolver={mappedComponents} onRender={RenderNode}>
			<CanvasContextProvider>
				<>
					{header}
					<Editor
						updatePageCallback={updatePageCallback}
						onDirty={onDirty}
						htmlTemplate={htmlTemplate}
					/>
				</>
			</CanvasContextProvider>
		</CraftJsEditor>
	);
};

PageEditor.defaultProps = {
	initialScreen: undefined,
	updatePageCallback: undefined,
	header: undefined,
	onDirty: undefined
};

export default PageEditor;
